<template>
    <div class="w-100">
        <v-card-title class="title font-weight-regular justify-space-between d-flex">
            &#xca;tes-vous sûr(e) de ne pas avoir la facture ?
            <v-avatar
                color="primary lighten-2"
                class="subheading white--text"
                size="24"
                v-text="step"
                v-if="step"
            />
        </v-card-title>
        <v-card-text>
            <v-form v-model="withoutInvoiceValid">
                <v-row>
                    <v-col cols="12" v-if="isAccountingUser && bankTransaction">
                        <v-text-field-filled
                            v-model="withoutInvoiceTitle"
                            label="Libellé"
                            :rules="[(t) => !!t || 'Libellé requis']"
                            density="compact"
                        />
                    </v-col>
                    <v-col cols="12">
                        <AccountableAccountVue
                            v-show="true"
                            :showLabel="true"
                            :showValidation="false"
                            :companyId="companyId"
                            :thirdPartyCompanyId="thirdPartyCompanyId"
                            :inAccountableAccountType="'SelectedTypes'"
                            :inAccountableAccountTypes="['Purchase', 'Sales']"
                            :inAccountableAccount="accountableAccount"
                            :inputStyle="'padding: 0'"
                            hideDetails
                            @select-accountableAccount="selectAccountableAccount"
                            :filled="true"
                        />
                    </v-col>
                    <v-col cols="12">
                        <v-text-field-filled
                            readonly
                            label="Société tiers"
                            hide-details
                            @click="dialogSelectCompany = !dialogSelectCompany"
                            :rules="[(v) => !!v || $t('settings.itemRequired')]"
                            class="mt-3"
                            :disabled="!accountableAccount"
                            :model-value="
                                thirdPartyCompany && thirdPartyCompany.name
                                    ? thirdPartyCompany.name
                                    : ''
                            "
                            density="compact"
                        />
                    </v-col>
                    <v-col cols="12">
                        <v-select-filled
                            v-model="vatRate"
                            :items="vatList"
                            :hide-details="true"
                            label="Taux de TVA"
                            v-if="showVAT"
                            class="mt-3"
                            density="compact"
                            item-title="text"
                        />
                    </v-col>
                    <v-col cols="6">
                        <oss-text-field
                            outlined
                            dense
                            content-class="mt-3 oss-"
                            v-if="vatRate !== -1 && bankTransaction"
                            label="Montant HT"
                            @update:model-value="onWithoutInvoiceNetAmountChanged"
                            :rules="withoutInvoiceNetAmountRules"
                            v-model="withoutInvoiceNetAmount"
                        >
                        </oss-text-field>
                    </v-col>
                    <v-col cols="6">
                        <oss-text-field
                            outlined
                            dense
                            content-class="mt-3 ml-3 oss-"
                            v-if="vatRate !== -1 && bankTransaction"
                            label="Montant TVA"
                            @update:model-value="onWithoutInvoiceVatAmountChanged"
                            :rules="withoutInvoiceVatAmountRules"
                            v-model="withoutInvoiceVatAmount"
                        >
                        </oss-text-field>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions v-if="!actions">
            <v-btn rounded @click="cancel()" style="margin: center" size="small">{{
                step ? "Précédent" : "Annuler"
            }}</v-btn>
            <v-spacer />
            <v-btn
                rounded
                @click="withoutInvoice"
                :loading="loading"
                :disabled="!withoutInvoiceValid"
                size="small"
                >Oui, je suis sûr(e)</v-btn
            >
        </v-card-actions>
        <oss-dialog
            v-model="dialogSelectCompany"
            :title="'Choisissez la société tiers'"
            v-if="dialogSelectCompany"
            width="800px"
        >
            <company-table
                :showGenericCompany="accountableAccount && accountableAccount.name.startsWith('6')"
                :showSupplierCompanies="
                    accountableAccount && accountableAccount.name.startsWith('6')
                "
                :showConsumerCompany="accountableAccount && accountableAccount.name.startsWith('7')"
                :showCustomerCompanies="
                    accountableAccount && accountableAccount.name.startsWith('7')
                "
                :showExistingCompanies="false"
                :showNewCompanies="true"
                :parentCompanyId="companyId"
                :company="thirdPartyCompany ? thirdPartyCompany.name : ''"
                :wizardMode="true"
                @select-company="companySelected"
            >
            </company-table>
        </oss-dialog>
    </div>
</template>

<script lang="ts">
import { User } from "../../store/store";

import { AccountableAccount } from "../../../../types";

import AccountableAccountVue from "../AccountableAccount.vue";
import CompanyTableComponent from "../admin/CompaniesTable.vue";

import CREATE_MULTI_WITHOUT_INVOICES from "../../graphql/reconciliation/CreateMultiWithoutInvoices.gql";
import CREATE_WITHOUT_INVOICE from "../../graphql/reconciliation/CreateWithoutInvoice.gql";

import { isNotSubjectToVAT } from "@/services/vat/vatHelpers.services";
import { Component, Prop, Vue, Watch, toNative } from "vue-facing-decorator";
import { createCompany } from "../../services/companyServices";
import { mathFix } from "../../services/mathServices";
import { vatRatesReconciliation } from "../../services/vatServices";

@Component({
    components: {
        AccountableAccountVue,
        companyTable: CompanyTableComponent,
    },
    emits: ["added", "cancel"],
})
export class WithoutInvoice extends Vue {
    @Prop({ default: null }) step: any;
    @Prop({ default: "" }) companyId: string;
    @Prop({ default: null }) btItem: any;
    @Prop({ default: null }) bts: any;
    @Prop({ default: null }) actions: any;

    loading: boolean = false;

    vatRate: number = -1;
    withoutInvoiceNetAmount: number = 0.0;
    withoutInvoiceVatAmount: number = 0.0;
    withoutInvoiceLoading = false;
    _withoutInvoiceTitle: string = "";

    withoutInvoiceValid: boolean = false;

    accountableAccount: AccountableAccount = null;

    dialogSelectCompany = false;
    thirdPartyCompany = null;

    // vatList = [{ value: -1, text: 'Sans TVA' }, { value: 20, text: '20 %' }, { value: 10, text: '10 %' }, { value: 8.5, text: '8.5 %' }, { value: 5.5, text: '5.5 %' }, { value: 2.1, text: '2.1 %' }, { value: 0, text: '0 %' }];
    // TAUX TVA 8.5 Done

    get currentUser(): User {
        return this.$store.state.user;
    }

    get isAccountingUser() {
        return this.currentUser.isAccountingUser;
    }

    get company() {
        return this.$store.getters.getCompany(this.companyId);
    }

    get bankTransaction() {
        if (this.btItem) {
            return this.btItem.bt;
        }
        return null;
    }

    get isPurchase() {
        const aa =
            this.accountableAccount && this.accountableAccount.name
                ? this.accountableAccount.name.substring(0, 1)
                : "";
        return aa === "6";
    }

    get isSales() {
        const aa =
            this.accountableAccount && this.accountableAccount.name
                ? this.accountableAccount.name.substring(0, 1)
                : "";
        return aa === "7";
    }

    get needThirdParty() {
        return this.isPurchase || this.isSales;
    }

    get vatList() {
        return vatRatesReconciliation;
    }

    beforeMount() {
        if (this.actions) {
            this.actions.action.title = "Oui, je suis sûr(e)";
            this.actions.action.disabled = () => {
                return !this.withoutInvoiceValid;
            };
            this.actions.action.function = this.withoutInvoice;
            this.actions.action.loading = () => {
                return this.loading;
            };
        }
    }

    modifyNetAmountFromVatAndAmount() {
        if (!this.bankTransaction) {
            return;
        }
        const amount = Math.abs(this.bankTransaction.amount);
        const netAmount = amount / (1.0 + this.vatRate / 100);
        this.withoutInvoiceNetAmount = mathFix(netAmount);
        if (this.withoutInvoiceNetAmount > netAmount) {
            this.withoutInvoiceNetAmount -= 0.01;
        }
        this.withoutInvoiceVatAmount = amount - netAmount;
    }

    @Watch("vatRate")
    onWithoutInvoiceVATChanged() {
        if (!this.bankTransaction) {
            return;
        }
        const amount = Math.abs(this.bankTransaction.amount);
        if (this.vatRate === -1) {
            this.withoutInvoiceNetAmount = amount;
            this.withoutInvoiceVatAmount = 0.0;
        } else {
            this.modifyNetAmountFromVatAndAmount();
        }
    }

    onWithoutInvoiceNetAmountChanged() {
        if (!this.bankTransaction) {
            return;
        }
        const amount = Math.abs(this.bankTransaction.amount);
        // const vat = amount - this.withoutInvoiceNetAmount
        if (this.withoutInvoiceNetAmount > amount) {
            this.withoutInvoiceNetAmount = amount;
        } else {
            const vat = (this.withoutInvoiceNetAmount * this.vatRate) / 100;
            if (this.withoutInvoiceNetAmount + vat > amount + 1.0e-3) {
                this.modifyNetAmountFromVatAndAmount();
            } else {
                this.withoutInvoiceVatAmount = vat;
            }
        }
    }

    onWithoutInvoiceVatAmountChanged() {
        if (!this.bankTransaction) {
            return;
        }
        const amount = Math.abs(this.bankTransaction.amount);

        const netAmount = this.withoutInvoiceVatAmount / (this.vatRate / 100);
        // const vat = amount - this.withoutInvoiceNetAmount
        if (netAmount > amount) {
            this.modifyNetAmountFromVatAndAmount();
        } else {
            this.withoutInvoiceNetAmount = netAmount;
        }
    }

    @Watch("bankTransaction")
    onBankTransactionChanged(vat) {
        if (this.bankTransaction) {
            this._withoutInvoiceTitle =
                this.bankTransaction.wordingDisplay || this.bankTransaction.wordingOriginal;
            this.onWithoutInvoiceVATChanged();
        } else {
            this._withoutInvoiceTitle = "";
            this.vatRate = -1;
        }
    }

    get withoutInvoiceTitle() {
        if (this._withoutInvoiceTitle) {
            return this._withoutInvoiceTitle;
        }
        if (this.bankTransaction) {
            return this.bankTransaction.wordingDisplay || this.bankTransaction.wordingOriginal;
        }
        return "";
    }

    set withoutInvoiceTitle(value) {
        this._withoutInvoiceTitle = value;
    }

    get withoutInvoiceNetAmountRules() {
        return [
            (netAmount) => {
                if (netAmount === "") return "Le montant HT est requis";
                const amount = Math.abs(this.bankTransaction.amount);
                const maxNetAmount = amount / (1.0 + this.vatRate / 100);
                return parseFloat(netAmount) <= maxNetAmount + 1e-3
                    ? true
                    : "Le montant HT doit inférieur à " + mathFix(maxNetAmount).toString();
            },
        ];
    }

    get withoutInvoiceVatAmountRules() {
        return [
            (vatAmount) => {
                if (vatAmount === "") return "Le montant de TVA est requis";
                const amount = Math.abs(this.bankTransaction.amount);
                const maxNetAmount = amount / (1.0 + this.vatRate / 100);
                return maxNetAmount + parseFloat(vatAmount) <= amount + 1e-3
                    ? true
                    : "Le montant HT doit inférieur à " + mathFix(maxNetAmount).toString();
            },
        ];
    }

    get firstBT() {
        if (this.btItem && this.btItem.bt) {
            return this.btItem.bt;
        }
        if (this.bts && this.bts.length) {
            return this.bts[0];
        }
        return null;
    }

    get thirdPartyCompanyId() {
        if (this.firstBT && this.firstBT.accountingLines) {
            for (const al of this.firstBT.accountingLines) {
                if (al.thirdPartyCompany) {
                    return al.thirdPartyCompany.id;
                }
            }
        }
        return "";
    }

    async withoutInvoice() {
        this.loading = true;
        let bts = [];
        if (this.btItem) {
            bts = [this.btItem.bt.id];
        } else if (this.bts && this.bts.length) {
            bts = this.bts.map((bt) => bt.id);
        }
        if (
            bts.length &&
            this.accountableAccount &&
            (!this.needThirdParty || this.thirdPartyCompany)
        ) {
            if (this.needThirdParty && !this.thirdPartyCompany.id && this.thirdPartyCompany.name) {
                this.thirdPartyCompany.id = await createCompany(
                    this.$apollo,
                    this.thirdPartyCompany,
                );
            }
            if (this.bts?.length) {
                await this.$apollo.mutate({
                    mutation: CREATE_MULTI_WITHOUT_INVOICES,
                    variables: {
                        bankTransactionIds: bts,
                        data: {
                            companyId: this.companyId,
                            vatRate: this.vatRate,
                            thirdPartyCompanyId:
                                this.thirdPartyCompany && this.thirdPartyCompany.id
                                    ? this.thirdPartyCompany.id
                                    : undefined,
                            accountableAccountName: this.accountableAccount.name,
                        },
                    },
                });
            } else {
                const amountSigned = this.btItem ? -this.btItem.accountingLine.amountSigned : 0;
                await this.$apollo.mutate({
                    mutation: CREATE_WITHOUT_INVOICE,
                    variables: {
                        wording: this.withoutInvoiceTitle,
                        bankTransactionIds: bts,
                        accountableAccountName: this.accountableAccount.name,
                        grossAmountSigned: amountSigned,
                        netAmount: this.withoutInvoiceNetAmount,
                        vatRate: this.vatRate,
                        thirdPartyCompanyId:
                            this.thirdPartyCompany && this.thirdPartyCompany.id
                                ? this.thirdPartyCompany.id
                                : undefined,
                    },
                });
            }
        }
        this.$emit("added");
        this.loading = false;
    }

    cancel() {
        this.$emit("cancel");
    }

    selectAccountableAccount(data) {
        const beforeAA =
            this.accountableAccount && this.accountableAccount.name
                ? this.accountableAccount.name.substring(0, 1)
                : "";
        const afterAA =
            data.accountableAccount && data.accountableAccount.name
                ? data.accountableAccount.name.substring(0, 1)
                : "";
        this.accountableAccount = data.accountableAccount;
        if (afterAA !== "6" && afterAA !== "7") {
            this.vatRate = -1;
            this.withoutInvoiceNetAmount = 0.0;
        }
        if (beforeAA !== afterAA) {
            this.thirdPartyCompany = null;
        }
        if (!this.isSales && !this.isPurchase) {
            this.thirdPartyCompany = null;
        }
    }

    get showVAT() {
        if (!this.isAccountingUser) {
            return false;
        }
        if (isNotSubjectToVAT(this.company.vatSystem)) {
            return false;
        }
        if (!this.accountableAccount) return true;
        if (
            this.accountableAccount.name.startsWith("6") ||
            this.accountableAccount.name.startsWith("7")
        ) {
            return true;
        }
        return false;
    }

    companySelected(company: any) {
        this.thirdPartyCompany = company;
        this.dialogSelectCompany = false;
    }
}

export default toNative(WithoutInvoice);
</script>
